import { useState } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useSEOMeta, generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEO";

const brandPartners = [
  {
    id: 1,
    name: "TechCare Africa",
    sector: "Technology",
    logo: "https://readdy.ai/api/search-image?query=modern%20tech%20company%20logo%20Africa%20clean%20minimal%20professional%20white%20background%20teal%20green%20geometric&width=240&height=140&seq=brand-1&orientation=landscape",
    description: "Providing digital infrastructure and technology solutions to support BHODEV's data management and community outreach programs.",
    contribution: "Digital Infrastructure",
    since: "2021",
    impact: "Digitized health records for 15,000+ patients",
  },
  {
    id: 2,
    name: "GreenBuild Solutions",
    sector: "Construction",
    logo: "https://readdy.ai/api/search-image?query=construction%20company%20logo%20green%20sustainable%20building%20clean%20minimal%20professional%20white%20background%20modern&width=240&height=140&seq=brand-2&orientation=landscape",
    description: "Supporting the construction and renovation of health centers and community facilities across the Southwest Region.",
    contribution: "Infrastructure Development",
    since: "2020",
    impact: "Built 4 community health centers",
  },
  {
    id: 3,
    name: "MediSupply Cameroon",
    sector: "Healthcare",
    logo: "https://readdy.ai/api/search-image?query=medical%20supply%20company%20logo%20healthcare%20clean%20minimal%20professional%20white%20background%20red%20cross%20health&width=240&height=140&seq=brand-3&orientation=landscape",
    description: "Donating essential medical supplies, equipment, and pharmaceuticals to support BHODEV's health programs.",
    contribution: "Medical Supplies",
    since: "2019",
    impact: "Supplied medicines to 8,000+ beneficiaries",
  },
  {
    id: 4,
    name: "EduPrint Press",
    sector: "Publishing",
    logo: "https://readdy.ai/api/search-image?query=publishing%20printing%20company%20logo%20education%20clean%20minimal%20professional%20white%20background%20book%20open%20pages&width=240&height=140&seq=brand-4&orientation=landscape",
    description: "Providing educational materials, printing services, and awareness campaign resources for BHODEV's literacy programs.",
    contribution: "Educational Materials",
    since: "2022",
    impact: "Distributed 25,000+ educational booklets",
  },
  {
    id: 5,
    name: "AgroFresh Cameroon",
    sector: "Agriculture",
    logo: "https://readdy.ai/api/search-image?query=agriculture%20food%20company%20logo%20fresh%20green%20leaf%20clean%20minimal%20professional%20white%20background%20farm%20organic&width=240&height=140&seq=brand-5&orientation=landscape",
    description: "Supporting food security programs and nutrition initiatives for vulnerable communities in Cameroon.",
    contribution: "Food Security",
    since: "2020",
    impact: "Fed 3,000+ families during crisis periods",
  },
  {
    id: 6,
    name: "SolarPower Africa",
    sector: "Energy",
    logo: "https://readdy.ai/api/search-image?query=solar%20energy%20company%20logo%20Africa%20clean%20minimal%20professional%20white%20background%20sun%20yellow%20orange%20renewable&width=240&height=140&seq=brand-6&orientation=landscape",
    description: "Installing solar energy systems in remote health facilities and community centers to ensure reliable power supply.",
    contribution: "Renewable Energy",
    since: "2021",
    impact: "Powered 6 off-grid health facilities",
  },
  {
    id: 7,
    name: "ConnectTel Networks",
    sector: "Telecommunications",
    logo: "https://readdy.ai/api/search-image?query=telecommunications%20network%20company%20logo%20clean%20minimal%20professional%20white%20background%20signal%20wave%20connectivity&width=240&height=140&seq=brand-7&orientation=landscape",
    description: "Providing internet connectivity and communication infrastructure to support BHODEV's remote operations.",
    contribution: "Connectivity",
    since: "2022",
    impact: "Connected 12 remote community centers",
  },
  {
    id: 8,
    name: "SafeWater Initiative",
    sector: "Water & Sanitation",
    logo: "https://readdy.ai/api/search-image?query=water%20sanitation%20company%20logo%20clean%20minimal%20professional%20white%20background%20water%20drop%20blue%20clean&width=240&height=140&seq=brand-8&orientation=landscape",
    description: "Partnering on clean water access and sanitation improvement projects in underserved communities.",
    contribution: "WASH Programs",
    since: "2019",
    impact: "Provided clean water to 10,000+ people",
  },
];

const sectors = ["All Sectors", "Technology", "Healthcare", "Construction", "Agriculture", "Energy", "Telecommunications", "Water & Sanitation", "Publishing"];

const stats = [
  { value: "8+", label: "Brand Partners", icon: "ri-building-2-line" },
  { value: "50K+", label: "People Impacted", icon: "ri-group-line" },
  { value: "2019", label: "First Partnership", icon: "ri-calendar-line" },
  { value: "8", label: "Sectors Covered", icon: "ri-pie-chart-line" },
];

export default function BrandPartners() {
  useSEOMeta({
    title: "Brand Partners - BHODEV Cameroon | Corporate Partnerships for Impact",
    description:
      "Leading companies partnering with BHODEV Cameroon to drive sustainable impact. Technology, healthcare, construction, agriculture, and energy companies supporting health and development programs across Cameroon.",
    keywords:
      "BHODEV brand partners, corporate partnership Cameroon, CSR Cameroon, NGO corporate partners, BHODEV corporate, business partnership Cameroon, social impact Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/partners-donors/brand-partners",
    ogImage:
      "https://readdy.ai/api/search-image?query=corporate%20business%20partnership%20handshake%20meeting%20modern%20office%20boardroom%20professional%20diverse%20team%20collaboration%20brand%20partnership%20NGO%20Africa%20Cameroon%20warm%20lighting&width=1200&height=630&seq=brandpartners-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Partners & Donors", url: "https://bhodevcameroon.org/partners-donors" },
        { name: "Brand Partners", url: "https://bhodevcameroon.org/partners-donors/brand-partners" },
      ]),
      ...generateWebPageSchema(
        "Brand Partners - BHODEV Cameroon",
        "Leading companies partnering with BHODEV Cameroon to drive sustainable impact across health, education, and community development.",
        "https://bhodevcameroon.org/partners-donors/brand-partners"
      ),
    },
  });

  const [activeSector, setActiveSector] = useState("All Sectors");
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  const filtered = activeSector === "All Sectors"
    ? brandPartners
    : brandPartners.filter((p) => p.sector === activeSector);

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[480px] md:h-[560px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=corporate%20business%20partnership%20handshake%20meeting%20modern%20office%20boardroom%20professional%20diverse%20team%20collaboration%20brand%20partnership%20NGO%20Africa%20Cameroon%20warm%20lighting&width=1440&height=560&seq=brand-hero-1&orientation=landscape"
          alt="Brand Partners"
          title="BHODEV Brand Partners - Corporate Partnerships for Sustainable Impact"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>
        <div
          ref={heroRef}
          className={`relative z-10 text-center px-4 w-full transition-all duration-1000 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-5">
            <a href="/" className="hover:text-white transition-colors duration-200">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/partners-donors" className="hover:text-white transition-colors duration-200">Partners &amp; Donors</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-primary">Brand Partners</span>
          </nav>
          <span className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-5 backdrop-blur-sm">
            <i className="ri-building-2-line"></i> Corporate Partnerships
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight font-playfair">
            Brand Partners
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Leading companies and organizations partnering with BHODEV to drive sustainable impact across Cameroon.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="#partners-grid"
              className="bg-primary text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
            >
              View All Partners
            </a>
            <a
              href="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Become a Partner
            </a>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-primary py-10 px-4 md:px-8 lg:px-12">
        <div
          ref={statsRef}
          className={`max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-700 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="text-center"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mx-auto mb-3">
                <i className={`${stat.icon} text-white text-lg`}></i>
              </div>
              <p className="text-3xl font-bold text-white font-playfair">{stat.value}</p>
              <p className="text-white/70 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Partners Grid */}
      <section id="partners-grid" className="py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={gridRef}
            className={`transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="text-center mb-12">
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">Our Corporate Network</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair">Our Brand Partners</h2>
              <p className="text-stone-500 max-w-xl mx-auto text-sm leading-relaxed">
                These companies share our vision of a healthier, more equitable Cameroon and contribute their expertise, resources, and networks to our mission.
              </p>
            </div>

            {/* Sector filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {sectors.map((sector) => (
                <button
                  key={sector}
                  onClick={() => setActiveSector(sector)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSector === sector
                      ? "bg-primary text-white shadow-lg shadow-primary/25"
                      : "bg-white text-stone-600 border border-stone-200 hover:border-primary hover:text-primary"
                  }`}
                >
                  {sector}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
              {filtered.map((partner, i) => (
                <div
                  key={partner.id}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-primary/20 transition-all duration-300 group cursor-pointer"
                  style={{ transitionDelay: `${i * 80}ms` }}
                  onMouseEnter={() => setHoveredId(partner.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div className="relative h-40 bg-stone-50 overflow-hidden">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className={`w-full h-full object-cover transition-transform duration-500 ${hoveredId === partner.id ? "scale-105" : "scale-100"}`}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold px-3 py-1 rounded-full border border-primary/20">
                        {partner.sector}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="bg-stone-900/70 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                        Since {partner.since}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-stone-900 text-lg mb-2 font-playfair">{partner.name}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed mb-4">{partner.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <div>
                        <p className="text-xs text-stone-400 mb-0.5">Contribution Area</p>
                        <p className="text-sm font-semibold text-stone-700">{partner.contribution}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-stone-400 mb-0.5">Impact</p>
                        <p className="text-xs font-semibold text-primary">{partner.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Become a Brand Partner */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={ctaRef}
            className={`transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-primary text-sm font-semibold tracking-widest uppercase">Join Our Network</span>
                <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-5 font-playfair leading-tight">
                  Become a Brand Partner
                </h2>
                <p className="text-stone-500 text-base leading-relaxed mb-6">
                  When joining forces with us, your company can tap into a wide range of programs and agenda-setting topics: human rights, climate change initiatives, education and self-reliance programs, food &amp; water programs, LGBT+ rights and many more.
                </p>
                <div className="flex flex-col gap-4 mb-8">
                  {[
                    { icon: "ri-check-double-line", text: "Tailored partnership packages to fit your CSR strategy" },
                    { icon: "ri-check-double-line", text: "Co-branding opportunities and visibility across our platforms" },
                    { icon: "ri-check-double-line", text: "Direct impact reporting and beneficiary stories" },
                    { icon: "ri-check-double-line", text: "Employee engagement and volunteering opportunities" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className={`${item.icon} text-primary`}></i>
                      </div>
                      <p className="text-stone-600 text-sm">{item.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-white px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-send-line"></i> Get in Touch
                  </a>
                  <a
                    href="/partners-donors"
                    className="inline-flex items-center justify-center gap-2 border border-stone-200 text-stone-700 px-7 py-3.5 rounded-full font-semibold text-sm hover:border-primary hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i> All Partners
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=corporate%20social%20responsibility%20CSR%20team%20community%20project%20Africa%20Cameroon%20outdoor%20health%20education%20warm%20sunlight%20diverse%20professionals%20working%20together%20NGO%20impact&width=600&height=500&seq=brand-cta-1&orientation=portrait"
                  alt="Become a Brand Partner"
                  title="Become a BHODEV Brand Partner - CSR Cameroon"
                  className="w-full h-96 lg:h-[480px] object-cover object-top rounded-2xl"
                />
                <div className="absolute -bottom-4 -left-4 bg-primary text-white rounded-2xl p-5 shadow-xl">
                  <p className="text-3xl font-bold font-playfair">8+</p>
                  <p className="text-white/80 text-xs mt-1">Active Brand Partners</p>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-stone-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-secondary rounded-lg">
                      <i className="ri-heart-line text-primary text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-stone-900">50K+ Lives</p>
                      <p className="text-xs text-stone-400">Impacted Together</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
